import React from 'react';
import { VIEW } from './siteLayout';

/**
 * How a site plan is DRAWN, per theme (backend plan.SITE_THEMES) - all plain SVG, no images:
 *   floor      an architect's blueprint: blue sheet, white walls the rooms share, door swings,
 *              furniture outlines, a title block and north arrow
 *   warehouse  an industrial site plan: concrete floor, zones marked in yellow with racking rows,
 *              the main forklift aisle with hazard edging, dock doors and trailers on the south wall
 *   region     a map in Governor's Console's style: parchment land in a sea, a river, mountains and
 *              forests, places as little towns on banners joined by a winding road
 * Each theme gives: Defs, Backdrop (fixed), Ground (moves with the zoom), Place (one room/town),
 * Glow (where to go), pinsAt (where its mission markers sit), Overlay (title, legend - fixed).
 */
const zoneLetter = (i) => String.fromCharCode(65 + i);
const fit = (text, chars) => (text.length > chars ? `${text.slice(0, chars - 1)}…` : text);

// ---------------------------------------------------------------- floor plan (blueprint)
// Rooms below the corridor have their door (and its swing) on the top wall: their label starts lower.
const labelY = (room) => (room.top ? 30 : 58);
const floor = {
  Defs: () => (
    <>
      <pattern id="bp-grid" width="15" height="15" patternUnits="userSpaceOnUse"><path d="M15 0H0V15" className="bp-grid" /></pattern>
      <pattern id="bp-grid-major" width="75" height="75" patternUnits="userSpaceOnUse"><path d="M75 0H0V75" className="bp-grid-major" /></pattern>
    </>
  ),
  Backdrop: () => (
    <>
      <rect width={VIEW.w} height={VIEW.h} className="bp-sheet" />
      <rect width={VIEW.w} height={VIEW.h} fill="url(#bp-grid)" />
      <rect width={VIEW.w} height={VIEW.h} fill="url(#bp-grid-major)" />
    </>
  ),
  Ground: ({ layout }) => {
    const { building: b, corridor: c } = layout;
    return (
      <>
        <rect x={b.x1} y={b.y1} width={b.x2 - b.x1} height={b.y2 - b.y1} className="bp-outer" />
        <rect x={b.x1 - 6} y={c.y - c.half + 8} width="12" height={c.half * 2 - 16} className="bp-cut" />
        <text x={(b.x1 + b.x2) / 2} y={c.y + 4} textAnchor="middle" className="bp-hall">C O R R I D O R</text>
        <path d={`M${b.x1 - 58} ${c.y} H${b.x1 - 14} M${b.x1 - 24} ${c.y - 7} L${b.x1 - 14} ${c.y} L${b.x1 - 24} ${c.y + 7}`} className="bp-arrow" />
        <text x={b.x1 - 36} y={c.y + 24} textAnchor="middle" className="bp-small">ENTRY</text>
      </>
    );
  },
  Place: ({ room, index }) => {
    const { x, y, w, h, door, top } = room;
    const hx = door.x - 16;
    const swing = top ? `M${hx} ${door.y} V${door.y - 30} A30 30 0 0 1 ${hx + 30} ${door.y}` : `M${hx} ${door.y} V${door.y + 30} A30 30 0 0 0 ${hx + 30} ${door.y}`;
    const fx = x + w * 0.6;
    const fy = top ? y + h - 64 : y + h - 56;
    return (
      <>
        <rect x={x} y={y} width={w} height={h} className="bp-room" />
        <rect x={door.x - 16} y={door.y - 3} width="32" height="6" className="bp-cut" />
        <path d={swing} className="bp-door" />
        <g className="bp-furniture">
          <rect x={fx} y={fy} width={w * 0.28} height="16" rx="1" />
          <circle cx={fx + w * 0.14} cy={fy + 28} r="6" />
          {w > 260 && <circle cx={fx - 34} cy={fy + 12} r="13" />}
        </g>
        <text x={x + 16} y={y + labelY(room)} className="bp-label">{fit(room.name.toUpperCase(), Math.floor(w / 11))}</text>
        <text x={x + 16} y={y + labelY(room) + 16} className="bp-small">ROOM {101 + index}</text>
      </>
    );
  },
  Glow: ({ room }) => <rect x={room.x + 3} y={room.y + 3} width={room.w - 6} height={room.h - 6} className="sm-glow" />,
  pinsAt: (room) => ({ x: room.x + 30, y: room.y + labelY(room) + 46 }),
  Overlay: ({ title, legend }) => (
    <>
      <g transform="translate(604 510)">
        <rect width="256" height="80" className="bp-titleblock" />
        <line x1="0" y1="34" x2="256" y2="34" className="bp-titleline" />
        <line x1="170" y1="34" x2="170" y2="80" className="bp-titleline" />
        <text x="12" y="23" className="bp-title">{fit(title.toUpperCase(), 28)}</text>
        <text x="12" y="54" className="bp-small">FLOOR PLAN</text>
        <text x="12" y="70" className="bp-small">SHEET A-101</text>
        <text x="182" y="54" className="bp-small">SCALE</text>
        <text x="182" y="70" className="bp-small">1 : 100</text>
      </g>
      <g transform="translate(566 548)" className="bp-north">
        <circle r="18" /><path d="M0 -14 L7 8 L0 3 L-7 8 Z" /><text y="-22" textAnchor="middle">N</text>
      </g>
      {legend({ x: 70, y: 530, className: 'bp-legend' })}
    </>
  )
};

// ---------------------------------------------------------------- warehouse site plan
const warehouse = {
  Defs: () => (
    <>
      <pattern id="wh-concrete" width="40" height="40" patternUnits="userSpaceOnUse">
        <circle cx="6" cy="9" r="1" className="wh-speck" /><circle cx="27" cy="21" r="1.4" className="wh-speck" />
        <circle cx="15" cy="33" r="0.9" className="wh-speck" /><circle cx="35" cy="5" r="0.8" className="wh-speck" />
      </pattern>
      <pattern id="wh-hazard" width="14" height="14" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
        <rect width="14" height="14" className="wh-hz-yellow" /><rect width="7" height="14" className="wh-hz-black" />
      </pattern>
    </>
  ),
  Backdrop: () => (
    <>
      <rect width={VIEW.w} height={VIEW.h} className="wh-yard" />
      <rect width={VIEW.w} height={VIEW.h} fill="url(#wh-concrete)" />
    </>
  ),
  Ground: ({ layout }) => {
    const { building: b, corridor: c, rooms } = layout;
    const docks = rooms.filter((r) => !r.top).flatMap((r) => [r.x + r.w * 0.3, r.x + r.w * 0.7]);
    return (
      <>
        <rect x={b.x1 - 14} y={b.y1 - 16} width={b.x2 - b.x1 + 28} height={b.y2 - b.y1 + 32} className="wh-building" />
        <rect x={b.x1 - 14} y={c.y - c.half} width={b.x2 - b.x1 + 28} height={c.half * 2} className="wh-aisle" />
        <rect x={b.x1 - 14} y={c.y - c.half} width={b.x2 - b.x1 + 28} height="6" fill="url(#wh-hazard)" />
        <rect x={b.x1 - 14} y={c.y + c.half - 6} width={b.x2 - b.x1 + 28} height="6" fill="url(#wh-hazard)" />
        {Array.from({ length: Math.floor((b.x2 - b.x1) / 90) }, (_, i) => (
          <path key={i} d={`M${b.x1 + 40 + i * 90} ${c.y - 8} l10 8 l-10 8`} className="wh-chevron" />
        ))}
        <text x={b.x2 - 6} y={c.y + 4} textAnchor="end" className="wh-aisle-label">MAIN AISLE</text>
        <rect x={b.x1 - 20} y={c.y - c.half + 10} width="12" height={c.half * 2 - 20} className="wh-cut" />
        <rect x={b.x1 - 60} y={c.y - 66} width="36" height="14" rx="2" className="wh-exit" />
        <text x={b.x1 - 42} y={c.y - 56} textAnchor="middle" className="wh-exit-text">EXIT</text>
        <text x={b.x1 - 42} y={c.y + 24} textAnchor="middle" className="wh-small">STAFF DOOR</text>
        {docks.map((dx, i) => (
          <g key={dx}>
            <rect x={dx - 18} y={b.y2 + 10} width="36" height="10" className="wh-dock" />
            <rect x={dx - 14} y={b.y2 + 26} width="28" height="44" rx="2" className="wh-trailer" />
            <text x={dx} y={b.y2 + 86} textAnchor="middle" className="wh-small">DOCK {i + 1}</text>
          </g>
        ))}
      </>
    );
  },
  Place: ({ room, index }) => {
    const { x, y, w, h } = room;
    const tag = fit(`ZONE ${zoneLetter(index)} · ${room.name.toUpperCase()}`, Math.floor((w - 28) / 8.2));
    const rows = Math.max(1, Math.floor((h - 92) / 30));
    return (
      <>
        <rect x={x} y={y} width={w} height={h} className="wh-zone" />
        {Array.from({ length: rows }, (_, i) => {
          const ry = y + 82 + i * 30;
          return (
            <g key={i} className="wh-rack">
              <rect x={x + 18} y={ry} width={w - 36} height="14" />
              {Array.from({ length: Math.floor((w - 36) / 34) }, (_, k) => <line key={k} x1={x + 18 + (k + 1) * 34} y1={ry} x2={x + 18 + (k + 1) * 34} y2={ry + 14} />)}
            </g>
          );
        })}
        <rect x={x + 12} y={y + 12} width={Math.min(w - 24, tag.length * 8.2 + 16)} height="26" rx="2" className="wh-tag" />
        <text x={x + 20} y={y + 30} className="wh-tag-text">{tag}</text>
      </>
    );
  },
  Glow: ({ room }) => <rect x={room.x - 5} y={room.y - 5} width={room.w + 10} height={room.h + 10} rx="4" className="sm-glow" />,
  pinsAt: (room) => ({ x: room.x + 30, y: room.y + 60 }),
  Overlay: ({ title, legend }) => (
    <>
      <rect width={VIEW.w} height="44" className="wh-strip" />
      <text x="18" y="28" className="wh-strip-title">SITE PLAN</text>
      <text x="124" y="28" className="wh-strip-name">{fit(title, 40)}</text>
      {legend({ x: 560, y: 22, row: true, className: 'wh-legend' })}
    </>
  )
};

// ---------------------------------------------------------------- region map (Governor's Console style)
const LAND = 'M80 150 C140 70 300 85 390 105 C470 70 610 60 700 100 C790 120 860 170 865 260 C880 350 850 440 870 500 C840 560 700 575 600 548 C500 585 370 570 280 545 C190 560 90 540 70 470 C40 400 70 330 52 260 C40 210 55 180 80 150 Z';
const RIVER = 'M610 62 C600 120 655 150 640 205 C625 260 560 270 575 320 C590 370 690 400 700 470 C705 510 690 545 700 572';
const MOUNTAINS = [[400, 150], [455, 165], [690, 140], [722, 158]];
const FORESTS = [[140, 285], [360, 470], [580, 455], [860, 330]];

const region = {
  Defs: () => (
    <pattern id="rg-waves" width="36" height="18" patternUnits="userSpaceOnUse">
      <path d="M0 10 Q9 4 18 10 T36 10" className="rg-wave" />
    </pattern>
  ),
  Backdrop: () => (
    <>
      <rect width={VIEW.w} height={VIEW.h} className="rg-sea" />
      <rect width={VIEW.w} height={VIEW.h} fill="url(#rg-waves)" />
    </>
  ),
  Ground: ({ layout }) => {
    const road = layout.route.map((p, i) => `${i ? 'L' : 'M'}${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join(' ');
    return (
      <>
        <path d={LAND} className="rg-coast" />
        <path d={LAND} className="rg-land" />
        <path d={RIVER} className="rg-river" />
        {MOUNTAINS.map(([mx, my]) => (
          <g key={`${mx}`} className="rg-mountain"><path d={`M${mx - 20} ${my + 16} L${mx} ${my - 16} L${mx + 20} ${my + 16} Z`} /><path d={`M${mx - 6} ${my - 6} L${mx} ${my - 16} L${mx + 6} ${my - 6}`} className="rg-snow" /></g>
        ))}
        {FORESTS.map(([fx, fy]) => (
          <g key={`${fx}`} className="rg-forest">{[[0, 0], [12, 5], [-11, 6], [4, 12]].map(([dx, dy]) => <circle key={`${dx}${dy}`} cx={fx + dx} cy={fy + dy} r="7" />)}</g>
        ))}
        <path d={road} className="rg-road-bed" />
        <path d={road} className="rg-road" />
        <g transform={`translate(${layout.start.x} ${layout.start.y})`} className="rg-start">
          <line x1="0" y1="0" x2="0" y2="-30" /><path d="M0 -30 L18 -24 L0 -18 Z" /><text x="-18" y="5" textAnchor="end">Start</text>
        </g>
      </>
    );
  },
  Place: ({ room }) => {
    const { cx, cy } = room;
    const label = fit(room.name, 18);
    const L = Math.min(150, label.length * 8 + 24);
    const house = (dx, dy, s) => `M${cx + dx - 9 * s} ${cy + dy} v${-11 * s} l${9 * s} ${-9 * s} l${9 * s} ${9 * s} v${11 * s} Z`;
    return (
      <>
        <g className="rg-town"><path d={house(-14, -6, 0.85)} /><path d={house(0, -12, 1.1)} /><path d={house(14, -5, 0.8)} /></g>
        <rect x={cx - L / 2} y={cy + 2} width={L} height="22" rx="3" className="rg-banner" />
        <text x={cx} y={cy + 18} textAnchor="middle" className="rg-label">{label}</text>
      </>
    );
  },
  Glow: ({ room }) => <circle cx={room.cx} cy={room.cy - 16} r="30" className="sm-glow" />,
  pinsAt: (room) => ({ x: room.cx - ((room.missions.length - 1) * 28) / 2, y: room.cy + 42, center: true }),
  Overlay: ({ title, legend }) => (
    <>
      <g transform="translate(818 96)" className="rg-compass">
        <circle r="30" /><path d="M0 -28 L6 0 L0 28 L-6 0 Z" /><path d="M-28 0 L0 -6 L28 0 L0 6 Z" className="rg-compass-ew" /><text y="-36" textAnchor="middle">N</text>
      </g>
      <g transform="translate(30 530)">
        <rect width="300" height="54" rx="4" className="rg-cartouche" />
        <rect x="5" y="5" width="290" height="44" rx="3" className="rg-cartouche-inner" />
        <text x="150" y="26" textAnchor="middle" className="rg-cartouche-title">{fit(title, 30)}</text>
        <text x="150" y="42" textAnchor="middle" className="rg-cartouche-sub">A MAP OF THE REGION</text>
      </g>
      {legend({ x: 30, y: 22, className: 'rg-legend', row: true })}
    </>
  )
};

export const THEMES = { floor, warehouse, region };
export const THEME_NAMES = { floor: 'Floor plan', warehouse: 'Warehouse plan', region: 'Region map' };
